/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, ComponentProps } from 'preact';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { classNames } from '../utils/UNSAFE_classNames';
import { BaseButton } from '../UNSAFE_BaseButton';

export type ButtonDisplay = 'icons' | 'label' | 'all';

export type Props = {
  /**
   *The default slot is the button's text label. The oj-c-button element accepts plain text or DOM nodes as children for the default slot."
   */
  children?: ComponentChildren;

  /**
   * The startIcon is the button's start icon.
   */
  startIcon?: ComponentChildren;

  /**
   * The endIcon is the button's end icon.
   */
  endIcon?: ComponentChildren;

  /**
   * Styling can be used for customizing icon layout by limiting padding ("embedded")
   */
  styling?: 'default' | 'embedded';

  /**
   * The size indicates how large the button is rendered.
   */
  size?: ComponentProps<typeof BaseButton>['size'];

  /**
   * Display indicates whether the label, the icons, or all elements should be rendered by the button.
   */
  display?: ButtonDisplay;
};

export const compStyles = {
  text: cssProps`
    ${{
      overflowX: 'hidden',
      overflowY: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'inline-flex'
    }}
  `,

  icon: cssProps`
    ${{
      display: 'inline-flex',
      fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-button-icon-size)',
      fontWeight: 'normal',
      marginTop: 0,
      marginBottom: 0
    }}
  `,

  startText: cssProps`
    ${{
      marginInlineStart: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-text-to-edge-padding)'
    }}
  `,

  endText: cssProps`
    ${{
      marginInlineEnd: 'var(--oj-c-PRIVATE-DO-NOT-USE-base-button-text-to-edge-padding)'
    }}
  `,

  startIcon: cssProps`
    ${{
      marginInlineEnd: 'var(--oj-c-PRIVATE-DO-NOT-USE-button-icon-to-text-padding)',
      marginInlineStart: 'var(--oj-c-PRIVATE-DO-NOT-USE-button-icon-to-edge-padding)'
    }}
  `,

  startIconOnly: cssProps`
    ${{
      marginInlineEnd: 'var(--oj-c-PRIVATE-DO-NOT-USE-button-icon-to-edge-padding)',
      marginInlineStart: 'var(--oj-c-PRIVATE-DO-NOT-USE-button-icon-to-edge-padding)'
    }}
  `,

  endIcon: cssProps`
    ${{
      marginInlineStart: 'var(--oj-c-PRIVATE-DO-NOT-USE-button-icon-to-text-padding)',
      marginInlineEnd: 'var(--oj-c-PRIVATE-DO-NOT-USE-button-icon-to-edge-padding)'
    }}
  `,

  label: cssProps`
    ${{
      cursor: 'inherit',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%'
    }}
  `
};

const isNullOrUndefined = (
  value: ComponentChildren | null | undefined
): value is null | undefined => {
  return value === null || value === undefined;
};

export function ButtonLayout({
  size = 'md',
  display = 'all',
  styling = 'default',
  ...props
}: Props) {
  const showIcons = display === 'all' || display === 'icons';
  const showText = display === 'all' || display === 'label';
  const labelClass = compStyles.label;
  const isOnlyIcon = props.startIcon && (display === 'icons' || !props.children) && !props.endIcon;
  const isEmbedded = styling === 'embedded';
  const textStartIcon = !isNullOrUndefined(props.startIcon) && display === 'all';
  const textEndIcon = !isNullOrUndefined(props.endIcon) && display === 'all';
  const isTwoIcons = props.startIcon && props.endIcon;

  const endIconClass = `${compStyles.icon} oj-c-button-layout-${size} ${compStyles.endIcon}`;
  const startIconStyles = classNames([
    compStyles.icon,
    isEmbedded ? `oj-c-button-layout-embedded-${size}` : `oj-c-button-layout-${size}`,
    isOnlyIcon ? compStyles.startIconOnly : compStyles.startIcon
  ]);
  const textStyles = classNames([
    compStyles.text,
    textStartIcon && !isTwoIcons && compStyles.endText,
    textEndIcon && !isTwoIcons && compStyles.startText
  ]);

  return (
    <span class={labelClass}>
      {showIcons && props.startIcon && <span class={startIconStyles}>{props.startIcon}</span>}
      {showText && props.children && <span class={textStyles}> {props.children} </span>}
      {showIcons && props.endIcon && <span class={endIconClass}>{props.endIcon}</span>}
    </span>
  );
}
